import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSupabaseChatStore } from '@/hooks/useSupabaseChatStore';
import SimpleChatHeader from '@/components/chat/SimpleChatHeader';
import SimpleChatMessageList from '@/components/chat/SimpleChatMessageList';
import SimpleChatInput from '@/components/chat/SimpleChatInput';
import ModelSelector from '@/components/chat/ModelSelector';
import ChatDialogs from '@/components/chat/ChatDialogs';
import { Attachment, Message, AIModel, Chat as ChatType } from '@/types';
import { toast } from "@/hooks/use-toast";
import { useSupabaseStore } from '@/store/SupabaseStore';
import { FileText } from 'lucide-react';

import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { enableChatVFSAccess, disableChatVFSAccess } from '@/lib/osVFSBridge';
import { useAppStore } from '@/store';

import { useAuth } from '@/contexts/AuthContext';
import { sendMessageToModel, type AIConfig } from '@/services/ModelService';
import { v4 as uuidv4 } from 'uuid';
import { useCodeSnippets } from '@/hooks/supabase/useCodeSnippets';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile'; // Import useIsMobile
import TypingIndicator from '@/components/chat/TypingIndicator'; // Import TypingIndicator
import { useKnowledgeBaseSearch } from '@/hooks/chat/useKnowledgeBaseSearch'; // Add this import
import ReactMarkdown from 'react-markdown'; // Import ReactMarkdown
import remarkGfm from 'remark-gfm'; // Import remarkGfm
import { cleanMarkdownBold } from '@/lib/utils'; // Import cleanMarkdownBold
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { Presentation, Sheet } from 'lucide-react';
import { getProjectAiContext } from '@/lib/projectAiContext';
import {
  appendGemma4OnnxModel,
  loadGemma4ThinkingEnabled,
  saveGemma4ThinkingEnabled,
  supportsGemma4Thinking,
} from '@/lib/gemma4Onnx';
import { filterEnabledModels } from '@/lib/modelSettings';
import { appendChatMessageIfEnabled } from '@/lib/osIntegration';

const isValidUUID = (uuid: string): boolean => {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

interface ChatProps {
  chatIdOverride?: string;
  embeddedInMapleOS?: boolean;
  onInvalidChat?: (chatId?: string) => void;
}

const Chat: React.FC<ChatProps> = ({
  chatIdOverride,
  embeddedInMapleOS = false,
  onInvalidChat,
}) => {
  const { chatId: routeChatId } = useParams<{ chatId: string }>();
  const chatId = chatIdOverride ?? routeChatId;
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbeddedChat = embeddedInMapleOS || new URLSearchParams(location.search).get('embeddedInMapleOS') === 'true';
  const { incrementChatViews, addMessage: addMessageToDb, addTokensUsed, models: baseAvailableModels } = useSupabaseStore();
  const availableModels = useMemo(() => filterEnabledModels(appendGemma4OnnxModel(baseAvailableModels)), [baseAvailableModels]);

  const viewCountTracked = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [pendingArena, setPendingArena] = useState(false);
  const isMobile = useIsMobile(); // Use the hook
  const [gemmaThinkingEnabled, setGemmaThinkingEnabled] = useState(() => loadGemma4ThinkingEnabled());

  const { user: authUser } = useAuth();

  useEffect(() => {
    // Auto-create a new chat when no chatId is provided, using preferModel from URL (e.g., MapleOS)
    if (!chatId) {
      const params = new URLSearchParams(location.search);
      const preferModel = params.get('preferModel');

      if (authUser && preferModel) {
        // Find preferred model or fallback to free/first available
        const preferred = availableModels.find(m => m.id === preferModel);
        const fallback = preferred || availableModels.find(m => m.tier === 'free') || availableModels[0];

        if (!fallback) {
          toast({
            title: "No models available",
            description: "Could not find a model to start the chat.",
            variant: "destructive"
          });
          return;
        }

        (async () => {
          const newChat = await useSupabaseStore.getState().addChat(authUser.id, 'New Chat', fallback.id);
          if (newChat?.id) {
            navigate(`/chat/${newChat.id}`);
          } else {
            toast({
              title: "Chat creation failed",
              description: "Could not create a new chat.",
              variant: "destructive"
            });
          }
        })();
      }
    }
  }, [chatId, location.search, authUser, availableModels, navigate]);

  const {
    messages,
    isLoading,
    chatTitle,
    modelName,
    selectedModelId,
    modelId2: arenaSecondModelId, // Use from store
    isUsingKnowledgeBase,
    sendMessage,
    handleTitleChange,
    handleDeleteChat,
    handleModelChange,
    handleModel2Change, // Use from store
    clearMessages,
    initializeChat,
    setMessages,
    isMemoryUpdating,
  } = useSupabaseChatStore();

  // Move currentChat definition here, before any useEffects that use it
  const { chats, updateChat } = useSupabaseStore();
  const currentChat = useMemo(() => {
    return chats.find(chat => chat.id === chatId);
  }, [chats, chatId]);
  const currentProjectId = currentChat?.project_id;
  const [projectKbId, setProjectKbId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const selectedModel = useMemo(
    () => availableModels.find((model) => model.id === selectedModelId) || null,
    [availableModels, selectedModelId]
  );
  const gemmaThinkingAvailable = supportsGemma4Thinking(selectedModel);
  const visibleSwitchClassName = 'border-slate-300 data-[state=unchecked]:bg-slate-200 dark:border-slate-600 dark:data-[state=unchecked]:bg-slate-700';
  const headerToggleCardClassName = 'flex items-center gap-2 rounded-md border bg-card/80 px-2 py-1 shadow-sm';

  // Ensure a KB exists for the project and set projectKbId

  useEffect(() => {
    const ensureKb = async () => {
      if (!currentProjectId || !authUser) {
        setProjectKbId(null);
        setProjectName(null);
        return;
      }
      // Resolve project name
      const { data: projData } = await (supabase as any)
        .from('projects')
        .select('id, name')
        .eq('id', currentProjectId)
        .maybeSingle();
      const proj: any = projData;
      const name = proj?.name || 'Project';
      setProjectName(name);

      const kbName = `Project ${currentProjectId} KB`;
      const { data: kbExistingData } = await (supabase as any)
        .from('knowledge_bases')
        .select('id')
        .eq('created_by', authUser.id)
        .eq('name', kbName)
        .maybeSingle();

      const kbExisting: any = kbExistingData;
      let kbId = kbExisting?.id || null;
      if (!kbId) {
        const { data: kbNewData, error } = await (supabase as any)
          .from('knowledge_bases')
          .insert({
            name: kbName,
            description: `Knowledge base for project ${currentProjectId}`,
            organization_id: null,
            created_by: authUser.id,
            is_private: true,
          })
          .select('id')
          .single();
        const kbNew: any = kbNewData;
        if (!error) kbId = kbNew?.id || null;
      }
      setProjectKbId(kbId);
    };
    ensureKb();
  }, [currentProjectId, authUser?.id]);

  // Derive arenaMode directly from currentChat.type
  const arenaMode = currentChat?.type === 'arena'; // <--- Derived arenaMode

  // Move chatInGlobalStore definition here, before useEffects that use it
  const chatInGlobalStore = useSupabaseStore(state =>
    state.chats.find(c => c.id === chatId)
  );

  // Now all useEffects can safely use these variables
  useEffect(() => {
    if (chatInGlobalStore?.messages) {
      setMessages(chatInGlobalStore.messages);
    }
  }, [chatInGlobalStore?.messages, setMessages]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId)) {
      initializeChat(chatId);
    } else if (isEmbeddedChat) {
      onInvalidChat?.(chatId);
    } else {
      navigate('/');
    }
  }, [chatId, initializeChat, isEmbeddedChat, navigate, onInvalidChat]);

  // Add effect to reload messages when chat type changes to arena
  useEffect(() => {
    if (chatId && currentChat?.type === 'arena') {
      const { fetchChatMessages } = useSupabaseStore.getState();
      fetchChatMessages(chatId);
    }
  }, [chatId, currentChat?.type]);

  // Add effect to fix existing Arena messages with missing arena_order
  useEffect(() => {
    if (messages.length > 0 && arenaMode) { // Use derived arenaMode
      const arenaMessages = messages.filter(m => m.is_arena);
      const userMessagesWithMissingOrder = arenaMessages.filter(m => 
        m.role === 'user' && (m.arena_order === undefined || m.arena_order === null)
      );

      if (userMessagesWithMissingOrder.length > 0) {
        
        // Fix the messages in the local state
        const updatedMessages = messages.map(msg => {
          if (msg.role === 'user' && msg.is_arena && (msg.arena_order === undefined || msg.arena_order === null)) {
            return { ...msg, arena_order: 0 };
          }
          return msg;
        });
        
        setMessages(updatedMessages);
        
        // Also update in the global store
        useSupabaseStore.setState(state => ({
          chats: state.chats.map(chat =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: updatedMessages,
                }
              : chat
          ),
        }));
      }
    }
  }, [messages, arenaMode, chatId, setMessages]); // Use derived arenaMode

  // Add effect to restore arena message pairs when messages are loaded
  useEffect(() => {
    if (messages.length > 0 && arenaMode) { // Use derived arenaMode
      const arenaMessages = messages.filter(m => m.is_arena);
      const groups: Record<string, { userMessage?: Message; primaryResponse?: Message; secondaryResponse?: Message }> = {};
      
      // Add detailed logging for the grouping process
      for (const msg of arenaMessages) {

        if (!msg.arena_group_id) {
            continue;
        }
        if (!groups[msg.arena_group_id]) {
            groups[msg.arena_group_id] = {};
        }
        if (msg.arena_order === 0) {
          groups[msg.arena_group_id].userMessage = msg;
        } else if (msg.arena_order === 1) {
          groups[msg.arena_group_id].primaryResponse = msg;
        } else if (msg.arena_order === 2) {
          groups[msg.arena_group_id].secondaryResponse = msg;
        } else {
        }
      }
    
      
      const pairs = Object.values(groups)
        .filter(
          (
            pair
          ): pair is {
            userMessage: Message;
            primaryResponse?: Message;
            secondaryResponse?: Message;
          } => Boolean(pair.userMessage)
        )
        .sort((a, b) => {
          const t1 = new Date(a.userMessage.timestamp || 0).getTime();
          const t2 = new Date(b.userMessage.timestamp || 0).getTime();
          return t1 - t2;
        });

      setArenaMessagePairs(pairs);
    } else if (!arenaMode) { // Use derived arenaMode
      console.log('[Chat.tsx] Not in Arena mode or no messages, clearing arenaMessagePairs.');
      setArenaMessagePairs([]);
    }
  }, [messages, arenaMode]); // Use derived arenaMode

  const [arenaMessagePairs, setArenaMessagePairs] = useState<
    Array<{
      userMessage: Message;
      primaryResponse?: Message;
      secondaryResponse?: Message;
    }>
  >([]);

  const [isCodeEditorOpen, setIsCodeEditorOpen] = useState(false);
  const [isCodeHistoryOpen, setIsCodeHistoryOpen] = useState(false);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
  const codeSnippets = useRef<any[]>([]);
  const { fetchCodeSnippets } = useCodeSnippets(addMessageToDb);

  const fetchSnippets = useCallback(async () => {
    if (isCodeHistoryOpen || isCodeEditorOpen) {
      const snippets = await fetchCodeSnippets();
      const mapped = (snippets || []).map((snippet: any) => ({
        id: snippet.id,
        code: snippet.code,
        language: snippet.language,
        name: snippet.name,
        chatId: snippet.chat_id,
        timestamp: snippet.timestamp || snippet.created_at || new Date().toISOString(),
        userId: snippet.user_id,
        content: snippet.code,
      }));
      codeSnippets.current = mapped;
    }
  }, [isCodeHistoryOpen, isCodeEditorOpen, fetchCodeSnippets]);
  useEffect(() => { fetchSnippets(); }, [fetchSnippets]);

  useEffect(() => {
    // This useEffect now only handles the initial selection of the second model if not already set
    if (arenaMode && !arenaSecondModelId && availableModels.length > 1) {
      const alt = availableModels.find(m => m.id !== selectedModelId);
      if (alt) handleModel2Change(alt.id); // Use handler from store
    }
    if (!arenaMode) {
      setArenaMessagePairs([]);
    }
  }, [arenaMode, availableModels, selectedModelId, arenaSecondModelId, handleModel2Change]); // arenaMode is now derived

  const handleSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (arenaMode) {
      await handleArenaSendMessage(content, attachments);
      return;
    }

    if (!selectedModelId) {
      toast({ title: "Select a model", description: "Please select an AI model before sending a message.", variant: "destructive" });
      return;
    }

    await sendMessage(content, attachments);
  };

  const handleArenaSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (!chatId || !authUser) return;
    if (!selectedModelId || !arenaSecondModelId) {
      toast({ title: "Select two models", description: "Please select both models for Arena mode.", variant: "destructive" });
      return;
    }
    setPendingArena(true);

    const arenaGroupId = uuidv4();
    const { addMessageLocally, updateMessageLocally } = useSupabaseStore.getState();

    const primaryModel = availableModels.find(m => m.id === selectedModelId);
    const secondaryModel = availableModels.find(m => m.id === arenaSecondModelId);

    if (!primaryModel || !secondaryModel) {
      toast({ title: "Model error", description: "Could not find both models.", variant: "destructive" });
      setPendingArena(false);
      return;
    }

    const tempUserMessageId = uuidv4();
    const tempPrimaryResponseId = uuidv4();
    const tempSecondaryResponseId = uuidv4();

    let userMessage: Message = {
      id: tempUserMessageId,
      content,
      role: 'user',
      sender: 'user',
      timestamp: new Date().toISOString(),
      attachments,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: null,
      arena_order: 0,
    } as any;

    const pendingPrimaryResponse: Message = {
      id: tempPrimaryResponseId,
      content: '',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: primaryModel.id,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: primaryModel.id,
      arena_order: 1,
    } as any;

    const pendingSecondaryResponse: Message = {
      id: tempSecondaryResponseId,
      content: '',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: secondaryModel.id,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: secondaryModel.id,
      arena_order: 2,
    } as any;

    addMessageLocally(chatId, userMessage);
    addMessageLocally(chatId, pendingPrimaryResponse);
    addMessageLocally(chatId, pendingSecondaryResponse);

    try {
      const persistedUserMessage = await addMessageToDb(chatId, {
        content,
        role: 'user',
        sender: 'user',
        attachments,
        is_arena: true,
        arena_group_id: arenaGroupId,
        arena_model: null,
        arena_order: 0,
      });

      if (!persistedUserMessage) {
        throw new Error('Failed to save Arena prompt.');
      }

      userMessage = persistedUserMessage;
      updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);

      const projectAiContext = await getProjectAiContext(currentProjectId);
      const baseMessages: Message[] = projectAiContext
        ? [
            {
              id: uuidv4(),
              content: projectAiContext.systemPrompt,
              role: 'system',
              sender: 'ai',
              timestamp: new Date().toISOString(),
              chatId,
            } as Message,
            userMessage,
          ]
        : [userMessage];

      const runArenaModel = async (
        arenaModel: AIModel,
        order: 1 | 2,
        tempMessageId: string,
        basePendingMessage: Message
      ) => {
        try {
          const result = await sendMessageToModel(baseMessages, arenaModel, availableModels);
          const resolvedContent = result.content?.trim() || `No response returned from ${arenaModel.name}.`;

          if ((result.tokensUsed || 0) > 0) {
            await addTokensUsed(chatId, result.tokensUsed || 0);
          }

          const persistedResponse = await addMessageToDb(chatId, {
            content: resolvedContent,
            role: 'assistant',
            sender: 'ai',
            model: arenaModel.id,
            is_arena: true,
            arena_group_id: arenaGroupId,
            arena_model: arenaModel.id,
            arena_order: order,
          });

          if (!persistedResponse) {
            throw new Error(`Failed to save ${arenaModel.name} response.`);
          }

          updateMessageLocally(chatId, tempMessageId, persistedResponse);
        } catch (error: any) {
          updateMessageLocally(chatId, tempMessageId, {
            ...basePendingMessage,
            content: `Unable to get a response from ${arenaModel.name}${error?.message ? `: ${error.message}` : '.'}`,
          });
        }
      };

      await Promise.allSettled([
        runArenaModel(primaryModel, 1, tempPrimaryResponseId, pendingPrimaryResponse),
        runArenaModel(secondaryModel, 2, tempSecondaryResponseId, pendingSecondaryResponse),
      ]);
    } catch (err) {
      useSupabaseStore.setState(state => ({
        chats: state.chats.map(chat =>
          chat.id === chatId
            ? {
                ...chat,
                messages: (chat.messages || []).filter(
                  msg => ![tempUserMessageId, tempPrimaryResponseId, tempSecondaryResponseId].includes(msg.id)
                ),
              }
            : chat
        ),
      }));
      toast({ title: "Arena error", description: "Failed to get responses from both models or save to database.", variant: "destructive" });
    } finally {
      setPendingArena(false);
    }
  };

  const [messagesLoaded, setMessagesLoaded] = useState(false);
  useEffect(() => {
    if (chatId && messages.length > 0) {
      if (!messagesLoaded) setMessagesLoaded(true);
    }
  }, [messages, chatId, messagesLoaded]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId) && !viewCountTracked.current) {
      incrementChatViews(chatId);
      viewCountTracked.current = true;
    }
  }, [chatId, incrementChatViews]);

  const persistedArenaPairs = useMemo(() => {
    const arenaMessages = messages.filter(m => m.is_arena);
    const groups: Record<string, { userMessage?: Message; primaryResponse?: Message; secondaryResponse?: Message }> = {};
    for (const msg of arenaMessages) {
      if (!msg.arena_group_id) continue;
      if (!groups[msg.arena_group_id]) groups[msg.arena_group_id] = {};
      if (msg.arena_order === 0) {
        groups[msg.arena_group_id].userMessage = msg;
      } else if (msg.arena_order === 1) {
        groups[msg.arena_group_id].primaryResponse = msg;
      } else if (msg.arena_order === 2) {
        groups[msg.arena_group_id].secondaryResponse = msg;
      }
    }
    return Object.values(groups)
      .filter(pair => pair.userMessage)
      .sort((a, b) => {
        const t1 = new Date(a.userMessage?.timestamp || 0).getTime();
        const t2 = new Date(b.userMessage?.timestamp || 0).getTime();
        return t1 - t2;
      });
  }, [messages]);

  const nonArenaMessages = useMemo(() => messages.filter(m => !m.is_arena), [messages]);

  const handleRegenerateResponse = async (messageId: string) => {
    const messageIndex = messages.findIndex(m => m.id === messageId);

    if (messageIndex <= 0) {
      toast({ title: "Error", description: "Could not find the original prompt to regenerate.", variant: "destructive" });
      return;
    }

    const aiMessage = messages[messageIndex];
    const userPrompt = messages[messageIndex - 1];

    if (aiMessage.role !== 'assistant' || userPrompt.role !== 'user') {
      toast({ title: "Invalid Action", description: "Only AI responses to a user's prompt can be regenerated.", variant: "destructive" });
      return;
    }

    const updatedMessages = messages.filter(m => m.id !== messageId);
    setMessages(updatedMessages);

    const { error: deleteError } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);

    if (deleteError) {
      toast({ title: "Database Error", description: `Failed to delete the previous response: ${deleteError.message}`, variant: "destructive" });
      setMessages(messages);
      return;
    }

    await sendMessage(userPrompt.content, userPrompt.attachments);
  };

  const renderArenaMessages = () => (
    <div className="flex-1 overflow-y-auto px-2 py-4">
      <div className="flex flex-col gap-6">
        {[...persistedArenaPairs, ...arenaMessagePairs.filter(p => !persistedArenaPairs.some(pp => pp.userMessage?.id === p.userMessage?.id))]
          .map((pair, idx) => (
          <div key={pair.userMessage?.id || idx} className="border rounded-lg bg-muted/10 p-4">
            <div className="mb-2">
              <span className="font-semibold">You:</span>
              <div className="ml-2 text-sm">{pair.userMessage?.content}</div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="border rounded bg-background p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{availableModels.find(m => m.id === selectedModelId)?.name || "Model 1"}</Badge>
                </div>
                <div className="text-sm whitespace-pre-line prose dark:prose-invert max-w-none">
                  {pair.primaryResponse?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {cleanMarkdownBold(pair.primaryResponse.content)}
                    </ReactMarkdown>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground not-prose">
                      <span>Waiting for response</span>
                      <TypingIndicator />
                    </div>
                  )}
                </div>
              </div>
              <div className="border rounded bg-background p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{availableModels.find(m => m.id === arenaSecondModelId)?.name || "Model 2"}</Badge>
                </div>
                <div className="text-sm whitespace-pre-line prose dark:prose-invert max-w-none">
                  {pair.secondaryResponse?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {cleanMarkdownBold(pair.secondaryResponse.content)}
                    </ReactMarkdown>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground not-prose">
                      <span>Waiting for response</span>
                      <TypingIndicator />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {pendingArena && (
          <div className="flex items-center justify-center gap-2 text-center text-muted-foreground py-4">
            <span>Waiting for both models to respond</span>
            <TypingIndicator />
          </div>
        )}
        <div ref={messagesEndRef} className="h-0" />
      </div>
    </div>
  );

  const renderArenaControls = () => (
    <div className="flex items-center gap-4 py-2 px-4 border-b bg-muted/30">
      <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
        Arena Mode
      </Badge>
      <span className="text-sm">Compare two models side-by-side</span>
      <div className="flex items-center gap-2 ml-auto">
        <span className="text-xs text-muted-foreground">Second Model:</span>
        <ModelSelector
          models={availableModels.filter(m => m.id !== selectedModelId)}
          selectedModelId={arenaSecondModelId || ''}
          onModelChange={handleModel2Change}
        />
      </div>
    </div>
  );

  const handleArenaInput = (content: string, attachments?: Attachment[]) => {
    handleArenaSendMessage(content, attachments);
  };

  const handleNormalInput = async (content: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null) => {
    // Prefer project KB when available
    const useKbId = projectKbId ?? kbId ?? null;
    const useKbName = projectName ?? kbName ?? null;
    const gemmaThinkingConfig: AIConfig | undefined = gemmaThinkingAvailable && gemmaThinkingEnabled
      ? { enableThinking: true, max_tokens: 1024 }
      : undefined;
    await sendMessage(content, attachments, useKbId, useKbName, authUser, gemmaThinkingConfig);
  };

  const handleArenaToggle = () => {
    const newArenaMode = !arenaMode;

    if (newArenaMode && chatId && currentChat?.type !== 'arena') {
      (updateChat as any)(chatId, { type: 'arena' });
      if (useAppStore.getState().saveHistory) {
        ((supabase as any).from('chats').update({ type: 'arena' }).eq('id', chatId) as any).then(({ error }: any) => {
          if (error) console.error("Failed to update chat type to arena", error);
        });
      }
    } else if (!newArenaMode && chatId && currentChat?.type === 'arena') {
      (updateChat as any)(chatId, { type: 'user-ai' });
      if (useAppStore.getState().saveHistory) {
        ((supabase as any).from('chats').update({ type: 'user-ai' }).eq('id', chatId) as any).then(({ error }: any) => {

          if (error) console.error("Failed to update chat type from arena", error);
        });
      }
    }
  };

  const renderArenaToggle = () => {
    return (
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/20">
        <Button
          variant={arenaMode ? "default" : "outline"}
          size="sm"
          className={arenaMode ? "bg-purple-600 text-white" : ""}
          onClick={handleArenaToggle}
          disabled={isMobile}
        >
          {arenaMode ? "Arena Mode: ON" : "Enable Arena Mode"}
        </Button>
        {isMobile && (
          <span className="text-xs text-muted-foreground ml-2">
            (Not available on mobile)
          </span>
        )}
      </div>
    );
  };

  const inputHandler = arenaMode ? handleArenaInput : handleNormalInput;

  // NEW: Trust Mode state

  const [trustEnabled, setTrustEnabled] = useState<boolean>(false);

  useEffect(() => {
    const saved = localStorage.getItem('trust_mode_enabled');
    setTrustEnabled(saved === 'true');
  }, []);

  const handleToggleTrust = (value: boolean) => {
    setTrustEnabled(value);
    localStorage.setItem('trust_mode_enabled', value ? 'true' : 'false');
  };

  const renderChatHeader = () => (
    <div className="flex-shrink-0 bg-background border-b">
      {/* This will stay at the top */}
      <SimpleChatHeader 
        title={chatTitle}
        modelName={modelName}
        modelId={selectedModelId}
        onTitleChange={handleTitleChange}
        onDeleteChat={handleDeleteChat}
        projectId={currentProjectId}
      />
      {renderArenaToggle()}
      {arenaMode && renderArenaControls()}
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <div className="min-w-0 flex-1 sm:flex-none">
          <ModelSelector
            models={availableModels}
            selectedModelId={selectedModelId || ''}
            onModelChange={handleModelSelectorChange}
          />
        </div>
        {gemmaThinkingAvailable && (
          <div className={`${headerToggleCardClassName} shrink-0`}>
            <span className="text-sm font-medium whitespace-nowrap">Gemma thinking</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 p-0 hover:bg-muted">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    When enabled, Gemma requests a reasoning pass and returns both a Thinking section and a final Answer. This uses more tokens and can be slower.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch
              className={visibleSwitchClassName}
              checked={gemmaThinkingEnabled}
              onCheckedChange={(value) => {
                setGemmaThinkingEnabled(value);
                saveGemma4ThinkingEnabled(value);
              }}
            />
          </div>
        )}
        <div className={`${headerToggleCardClassName} shrink-0`}>
          <span className="text-sm font-medium whitespace-nowrap">Trust Mode</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 p-0 hover:bg-muted">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  When ON, prompts are checked for authorship, clarity, and presence. Roleplay/simulation is blocked, sensitive info is redacted, and generation parameters are capped for safer, consistent results.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch className={visibleSwitchClassName} checked={trustEnabled} onCheckedChange={handleToggleTrust} />
        </div>
      </div>

      {/* NEW: Quick open icons for MapleOS apps */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/30">
        <span className="text-xs text-muted-foreground">Open in MapleOS:</span>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={async () => {
            if (!chatId || !currentChat) return;
            // Ensure VFS mirror and compute conversation folder
            const folder = await enableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any, messages);
            const title = `AI Access - ${currentChat.title || chatTitle || 'Chat'}`;
            const url = `/desktop/start?open=maple-access&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(folder)}`;
            window.open(url, '_blank');
          }}
          title="Open in MapleWord"
        >
          <FileText size={16} className="" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 cursor-not-allowed pointer-events-none opacity-50"
          onClick={async () => {
            if (!chatId || !currentChat) return;
            const folder = await enableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any, messages);
            const convPath = `${folder}\\conversation.txt`;
            const title = `MapleSlide - ${currentChat.title || chatTitle || 'Chat'}`;
            const url = `/desktop/start?open=maple-slide&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(convPath)}`;
            window.open(url, '_blank');
          }}
          title="Open in MapleSlide"
        >
          <Presentation className="w-5 h-5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 opacity-50 cursor-not-allowed pointer-events-none"
          onClick={async () => {
            if (!chatId || !currentChat) return;
            const folder = await enableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any, messages);
            const convPath = `${folder}\\conversation.txt`;
            const title = `MapleSheet - ${currentChat.title || chatTitle || 'Chat'}`;
            const url = `/desktop/start?open=maple-sheet&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(convPath)}`;
            window.open(url, '_blank');
          }}
          title="Open in MapleSheet"
        >
          <Sheet className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );

  const handleInsertSnippetToChat = useCallback(
    async (snippet) => {
      if (!chatId) return;
  
      const attachment: Attachment = {
        id: Math.random().toString(36).substring(2, 11),
        type: 'code',
        content: snippet.code,
        name: snippet.name || `Code snippet (${snippet.language})`,
      };
  
      const message = `I've shared a code snippet in ${snippet.language}. Please analyze the attached code.`;

      await sendMessage(message, [attachment]);
      setIsCodeEditorOpen(false);
    },
    [chatId, sendMessage]
  );
  
  const handleModelSelectorChange = useCallback(
    (modelId: string) => {
      handleModelChange(modelId);
    },
    [handleModelChange]
  );

  useEffect(() => {
    if (chatInGlobalStore?.messages) {
      setMessages(chatInGlobalStore.messages);
    }
  }, [chatInGlobalStore?.messages, setMessages]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId)) {
      initializeChat(chatId);
    } else if (isEmbeddedChat) {
      onInvalidChat?.(chatId);
    } else {
      navigate('/');
    }
  }, [chatId, initializeChat, isEmbeddedChat, navigate, onInvalidChat]);

  // Add effect to reload messages when chat type changes to arena
  useEffect(() => {
    if (chatId && currentChat?.type === 'arena') {
      const { fetchChatMessages } = useSupabaseStore.getState();
      fetchChatMessages(chatId);
    }
  }, [chatId, currentChat?.type]);

  if (!chatId) {
    return (
      <div className="flex flex-col h-screen bg-background items-center justify-center">
        <div className="p-6 bg-card rounded-lg shadow-lg text-center max-w-md">
          <h2 className="text-xl font-semibold mb-4">Invalid Chat ID</h2>
          <p className="mb-4">The chat ID format is invalid. You will be redirected to the home page shortly.</p>
          <Button asChild className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Calculate padding for messages and bottom offset for input based on mobile navigation
  const mobileNavHeight = 56; // Height of MobileNavigation is h-14 (14 * 4 = 56px)
  const inputHeightEstimate = 74; // Estimate input height (p-3 + min-h-[50px] textarea)
  const messagesPaddingBottom = inputHeightEstimate + 16; // 16px for some visual buffer
  const inputBottomOffset = 0; // Input should be at the very bottom

  return (
    <>
      <div className="flex flex-col h-full bg-background relative"> {/* Main container */}
        {/* Header - flex-none by default */}
        {renderChatHeader()}
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {arenaMode
            ? renderArenaMessages()
            : (nonArenaMessages.length > 0 ? (
                <SimpleChatMessageList 
                  messages={nonArenaMessages} 
                  isLoading={isLoading}
                  messagesEndRef={messagesEndRef}
                  chatId={chatId}
                  hideCodeBlockIfAttachment
                  onRegenerate={handleRegenerateResponse}
                  isMemoryUpdating={isMemoryUpdating}
                />
              ) : isLoading ? (
                <div className="flex-1 flex items-center justify-center h-full">
                  <TypingIndicator />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center p-8 text-muted-foreground">
                  {/* Removed the h3 and p tags to hide the "Start a conversation" text */}
                </div>
              ))}
        </div>

        {/* Input Area - flex-none, naturally sits at bottom */}
        <div className="flex-none bg-background border-t p-3 z-10">
          <SimpleChatInput 
            onSendMessage={inputHandler}
            onClearChat={clearMessages}
            isLoading={isLoading || pendingArena}

            onOpenCodeEditor={() => setIsCodeEditorOpen(true)}
            onOpenCanvas={() => setIsCanvasOpen(true)}
            onOpenCodeHistory={() => setIsCodeHistoryOpen(true)}
            onFileUpload={async (file) => {
              if (!chatId) return;
              if (file.size > 10 * 1024 * 1024) {
                toast({
                  title: "File too large",
                  description: "Maximum file size is 10 MB",
                  variant: "destructive"
                });
                return;
              }
              const isImage = file.type.startsWith('image/');
              const isDocument = file.type === 'application/pdf' || 
                              file.type === 'text/plain' || 
                              file.type === 'text/csv' || 
                              file.name.endsWith('.pdf') || 
                              file.name.endsWith('.txt') || 
                              file.name.endsWith('.csv') || 
                              file.name.endsWith('.docx');
              if (!isImage && !isDocument) {
                toast({
                  title: "Unsupported file type",
                  description: "Please upload an image, PDF, TXT, CSV, or DOCX file",
                  variant: "destructive"
                });
                return;
              }
              const reader = new FileReader();
              reader.onload = async (event) => {
                if (event.target?.result) {
                  const attachment: Attachment = {
                    id: Math.random().toString(36).substring(2, 11),
                    type: isImage ? 'image' : 'file',
                    content: event.target.result as string,
                    name: file.name
                  };
                  await inputHandler(`Uploaded ${file.name}`, [attachment]);
                }
              };
              reader.readAsDataURL(file);
            }}
            onExportChat={() => {
              if (nonArenaMessages.length === 0) {
                return;
              }
              const exportText = nonArenaMessages.map(msg => {
                const sender = msg.role === 'user' ? 'You' : 'AI';
                return `${sender}: ${msg.content}`;
              }).join('\n\n');
              const blob = new Blob([exportText], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `chat-export-${new Date().toISOString().split('T')[0]}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            chatId={chatId}
          />
        </div>
      </div>
      <ChatDialogs
        isCodeEditorOpen={isCodeEditorOpen}
        setIsCodeEditorOpen={setIsCodeEditorOpen}
        isCodeHistoryOpen={isCodeHistoryOpen}
        setIsCodeHistoryOpen={setIsCodeHistoryOpen}
        isCanvasOpen={isCanvasOpen}
        setIsCanvasOpen={setIsCanvasOpen}
        isChatHistoryOpen={isChatHistoryOpen}
        setIsChatHistoryOpen={setIsChatHistoryOpen}
        onSaveCode={async (code, language) => {
          if (!chatId) return;
          const attachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'code',
            content: code,
            name: `Code snippet (${language})`
          };
          const codeBlock = `\`\`\`${language}\n${code}\n\`\`\``;
          await sendMessage(
            `I've shared a code snippet in ${language}:\n${codeBlock}`,
            [attachment]
          );
          setIsCodeEditorOpen(false);
        }}
        onSelectCodeSnippet={async (snippetId) => {
          const snippet = codeSnippets.current.find(s => s.id === snippetId);
          if (snippet) {
            const attachment: Attachment = {
              id: Math.random().toString(36).substring(2, 11),
              type: 'code',
              content: snippet.code,
              name: snippet.name
            };
            const codeBlock = `\`\`\`${snippet.language || ''}\n${snippet.code}\n\`\`\``;
            await sendMessage(
              `I've shared a code snippet in ${snippet.language}:\n${codeBlock}`,
              [attachment]
            );
            setIsCodeHistoryOpen(false);
          }
        }}
        onSaveCanvas={async (imageData) => {
          const attachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'canvas',
            content: imageData,
            name: 'Canvas drawing'
          };
          await sendMessage("I've shared a drawing:", [attachment]);
          setIsCanvasOpen(false);
        }}
        onSelectChatHistory={() => {
          return;
        }}
        codeSnippets={codeSnippets.current}
        onInsertSnippetToChat={handleInsertSnippetToChat}
      />
    </>
  );
};

export default Chat;